
import { Stack } from 'ui/stack';
import { Select } from 'ui/select';

export interface RegimeOption {
  value: string | number;
  label: string;
}

export interface RangeOption {
  value: string;
  label: string;
}

export interface RankOption {
  value: number;
  label: string;
}

export interface SpecialAwardRegimesSourceProps {
  regimeOptions: RegimeOption[];
  selectedRegimeIds: Array<string | number>;
  onRegimesChange: (ids: Array<string | number>) => void;
  rangeOptions: RangeOption[];
  selectedRangeKeys: string[];
  onRangesChange: (keys: string[]) => void;
  rangeLabel: string;
  rankOptions: RankOption[];
  selectedRankIds: number[];
  onRanksChange: (ids: number[]) => void;
  isRanksLoading?: boolean;
}

const toValues = <TOption extends { value: string | number }>(
  value: TOption | TOption[] | null
): TOption['value'][] => {
  if (!value) {
    return [];
  }

  return (Array.isArray(value) ? value : [value]).map((option) => option.value);
};

const SpecialAwardRegimesSource = ({
  regimeOptions,
  selectedRegimeIds,
  onRegimesChange,
  rangeOptions,
  selectedRangeKeys,
  onRangesChange,
  rangeLabel,
  rankOptions,
  selectedRankIds,
  onRanksChange,
  isRanksLoading = false,
}: SpecialAwardRegimesSourceProps) => {
  const selectedRegimeOptions = regimeOptions.filter((option) =>
    selectedRegimeIds.some((id) => String(id) === String(option.value))
  );
  const selectedRangeOptions = rangeOptions.filter((option) =>
    selectedRangeKeys.includes(option.value)
  );
  const selectedRankOptions = rankOptions.filter((option) =>
    selectedRankIds.includes(option.value)
  );

  const hasRegimes = selectedRegimeIds.length > 0;

  return (
    <Stack
      sx={{ gap: 1.5, pr: '54px' }}
      data-testid="special-award-regimes-source"
      data-modal-keyboard-ignore
    >
      <Select<RegimeOption>
        label="Regime"
        required
        multiple
        clearable
        options={regimeOptions}
        value={selectedRegimeOptions}
        onChange={(value) => onRegimesChange(toValues(value))}
        placeholder=""
        data-testid="special-award-regimes-select"
      />

      <Select<RangeOption>
        label={rangeLabel}
        multiple
        clearable
        options={rangeOptions}
        value={selectedRangeOptions}
        onChange={(value) => onRangesChange(toValues(value))}
        disabled={!hasRegimes}
        placeholder=""
        data-testid="special-award-ranges-select"
      />

      <Select<RankOption>
        label="Academic / Scholarship"
        multiple
        clearable
        options={rankOptions}
        value={selectedRankOptions}
        onChange={(value) => onRanksChange(toValues(value))}
        disabled={!hasRegimes}
        loading={isRanksLoading}
        placeholder=""
        data-testid="special-award-ranks-select"
      />
    </Stack>
  );
};

export default SpecialAwardRegimesSource;
